import useAppStore from "../store/appStateZustand";
import Select from "./Select";

/**
 * Clone Environment Selector
 *
 * A dropdown component for selecting which clone environment (1-10) to use.
 * Only visible when VITE_STAGE is "dev" or "local".
 *
 * When a clone is selected, it updates both the cloneEnvironment state
 * and the apiUrl to point to the correct SA-API clone.
 */

// Build the SA-API URL for a given clone environment
function getSaApiUrl(cloneEnvironment) {
  const defaultUrl = import.meta.env.VITE_API;

  if (!cloneEnvironment || cloneEnvironment < 1 || cloneEnvironment > 10) {
    return defaultUrl;
  }

  // Clone SA-API URL pattern: https://clone-{N}-sa-api.publishingdashboard.com/api/v1
  return `https://clone-${cloneEnvironment}-sa-api.publishingdashboard.com/api/v1`;
}

function CloneEnvironmentSelector() {
  const cloneEnvironment = useAppStore(state => state.cloneEnvironment);
  const setCloneEnvironment = useAppStore(state => state.setCloneEnvironment);

  // Only show in dev or local environments
  const stage = import.meta.env.VITE_STAGE;
  const showSelector = stage === "dev" || stage === "local";

  if (!showSelector) {
    return null;
  }

  const handleChange = (value) => {
    if (value === "default") {
      setCloneEnvironment("default");
      // Reset to default API URL from environment variables
      useAppStore.setState({ apiUrl: import.meta.env.VITE_API });
    }
    else {
      const cloneNum = Number.parseInt(value, 10);
      setCloneEnvironment(cloneNum);
      // Update API URL to point to clone SA-API
      useAppStore.setState({ apiUrl: getSaApiUrl(cloneNum) });
    }
  };

  // Build options for the Select component
  const options = [
    ...(stage === "local" ? [{ name: "Default (env var)", value: "default" }] : []),
    ...Array.from({ length: 10 }, (_, i) => ({
      name: `Clone ${i + 1}`,
      value: String(i + 1),
    })),
  ];

  const currentValue = cloneEnvironment === "default"
    ? "default"
    : cloneEnvironment
      ? String(cloneEnvironment)
      : (stage === "local" ? "default" : "");

  return (
    <Select
      id="cloneEnvSelect"
      name="cloneEnvSelect"
      className="custom-select text-old-sentric-dark-gray"
      options={options}
      value={currentValue}
      onChange={handleChange}
      placeholder="Environment"
      list
      authSearch
      required
    />
  );
}

export default CloneEnvironmentSelector;
